import { useEffect } from "react";
import { useCachedAppConfig } from "@/queries/appConfigCache";
import { locales } from "@/lib/i18n-config";
import { isLocaleCookiePersisted, setLocaleCookie } from "@/lib/locale-detection";

/**
 * Keeps the NEXT_LOCALE cookie in sync with the profile locale (the source of
 * truth). Covers sessions restored without a fresh login — e.g. a second device
 * that never saw the login-time cookie write. The reload only happens when the
 * cookie read-back confirms the write, so blocked cookies can't cause a loop.
 */
export function useProfileLocaleSync(activeLocale: string, reload?: () => void) {
    const profileLocale = useCachedAppConfig()?.user.locale;

    useEffect(() => {
        if (
            !profileLocale ||
            !locales.includes(profileLocale as (typeof locales)[number]) ||
            profileLocale === activeLocale
        ) {
            return;
        }

        setLocaleCookie(profileLocale);

        // Raw cookie comparison: getLocale() falls back to the default locale,
        // which would vacuously confirm a failed write for "en" profiles and
        // reload forever when cookies are blocked
        if (isLocaleCookiePersisted(profileLocale)) {
            (reload ?? (() => window.location.reload()))();
        }
    }, [profileLocale, activeLocale, reload]);
}
